import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Grid,
  Tooltip
} from '@mui/material'
import {
  AddressSlotEditor,
  CenteredCircularProgress,
  EditActions,
  FAX_TYPE_ID,
  PHONE_TYPE_ID,
  PhoneFaxSection,
  usAddressIsValid,
  useBuildingLuts,
  useGlobalMessageHandler
} from 'components'
import {
  type Building,
  type ExecOrgAddress,
  type ExecOrgPhone,
  getExecOrgAddress,
  updateExecOrgAddress,
  type UsAddress
} from 'services'
import { formatErrorFieldMessages } from 'interfaces'

interface ExecutiveOrgAddressEditorProps {
  orgId: number
  parentOrgId?: number | null
  parentOrgName?: string
  filterBuildings?: (buildings: Building[]) => Building[]
  onContactStatus?: (status: { hasAddress: boolean, hasPhone: boolean }) => void
}

const emptyAddressSlot = (): UsAddress => ({
  building: '',
  room: '',
  addressOne: '',
  addressTwo: '',
  city: '',
  state: { id: 0 },
  country: { id: 223, country_desc: '', country_code: '', states: [] },
  zipCode: ''
})

export const emptyExecOrgAddress = (orgId: number): ExecOrgAddress => ({
  id: 0,
  execOrgId: orgId,
  typeId: 1,
  primary: emptyAddressSlot(),
  secondary: emptyAddressSlot(),
  mailingAddress: 1,
  phones: [],
  displayOrder: 1
})

export const newExecOrgPhone = (orgId: number, addressId: number, isPhone: boolean): ExecOrgPhone => ({
  id: -Math.floor(Math.random() * 900000 + 100000),
  execOrgId: orgId,
  addressId,
  typeId: isPhone ? PHONE_TYPE_ID : FAX_TYPE_ID,
  description: isPhone ? 'Main Phone' : 'Main Fax',
  areaCode: '',
  countryCode: '+1',
  phoneNumber: '',
  extension: '',
  displayOrder: 1
})

const slotFilled = (slot: UsAddress): boolean =>
  (slot.addressOne ?? '').trim() !== '' || (slot.city ?? '').trim() !== ''

export const execOrgAddressHasContent = (address: ExecOrgAddress): boolean =>
  slotFilled(address.primary) || slotFilled(address.secondary) || address.phones.length > 0

export const buildContactFromParent = (target: ExecOrgAddress, parent: ExecOrgAddress, orgId: number): ExecOrgAddress => {
  const fill = (base: ExecOrgPhone, from?: ExecOrgPhone): ExecOrgPhone =>
    from == null
      ? base
      : { ...base, areaCode: from.areaCode, phoneNumber: from.phoneNumber, countryCode: from.countryCode, extension: from.extension }
  return {
    ...target,
    primary: { ...parent.primary },
    secondary: { ...parent.secondary },
    mailingAddress: parent.mailingAddress,
    phones: [
      fill(newExecOrgPhone(orgId, target.id, true), parent.phones.find(phone => phone.typeId === PHONE_TYPE_ID)),
      fill(newExecOrgPhone(orgId, target.id, false), parent.phones.find(phone => phone.typeId === FAX_TYPE_ID))
    ]
  }
}

export const ExecutiveOrgAddressEditor: React.FC<ExecutiveOrgAddressEditorProps> = ({ orgId, parentOrgId, parentOrgName, filterBuildings, onContactStatus }) => {
  const [address, setAddress] = useState<ExecOrgAddress | null>(null)
  const [savedAddress, setSavedAddress] = useState<ExecOrgAddress | null>(null)
  const [parentAddress, setParentAddress] = useState<ExecOrgAddress | null>(null)
  const [loading, setLoading] = useState(true)
  const [disabled, setDisabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const { lutValues: buildingLutValues, loading: buildingLoading } = useBuildingLuts()
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  useEffect(() => {
    setLoading(true)
    getExecOrgAddress(orgId)
      .then(loaded => {
        const initial = loaded ?? emptyExecOrgAddress(orgId)
        setAddress(initial)
        setSavedAddress(initial)
      })
      .catch(() => {
        // Fall back to an empty address so the card stays usable after a
        // transient load error instead of spinning forever.
        showErrorMessage('Failed to load organization address.')
        const fallback = emptyExecOrgAddress(orgId)
        setAddress(fallback)
        setSavedAddress(fallback)
      })
      .finally(() => { setLoading(false) })
  }, [orgId])

  useEffect(() => {
    if (parentOrgId == null) {
      setParentAddress(null)
      return
    }
    getExecOrgAddress(parentOrgId)
      .then(setParentAddress)
      .catch(() => { setParentAddress(null) })
  }, [parentOrgId])

  // Report contact completeness to the detail page's missing-contact banner
  // (TI-4100). Fires on load and after each save (savedAddress changes).
  useEffect(() => {
    if (savedAddress == null || onContactStatus == null) return
    const hasAddress =
      (savedAddress.primary.addressOne ?? '').trim() !== '' || (savedAddress.primary.city ?? '').trim() !== ''
    const hasPhone = savedAddress.phones.some(
      phone => phone.typeId === PHONE_TYPE_ID && (phone.phoneNumber ?? '').trim() !== ''
    )
    onContactStatus({ hasAddress, hasPhone })
  }, [savedAddress, onContactStatus])

  if (loading || address == null) {
    return <CenteredCircularProgress loading={true} size={40} position='relative' />
  }

  const phone = address.phones.find(item => item.typeId === PHONE_TYPE_ID)
  const fax = address.phones.find(item => item.typeId === FAX_TYPE_ID)
  const buildings = filterBuildings != null
    ? filterBuildings(buildingLutValues.buildings)
    : buildingLutValues.buildings

  const updateSlot = (slot: 'primary' | 'secondary', value: UsAddress): void => {
    setAddress({ ...address, [slot]: value })
  }

  // Target the single displayed phone/fax by id (not by type), so the edit
  // can never fan out across multiple rows of the same type.
  const handlePhoneChange = (isFax: boolean, field: keyof ExecOrgPhone, value: string): void => {
    const targetId = (isFax ? fax : phone)?.id
    if (targetId == null) return
    const updatedPhones = address.phones.map(item =>
      item.id === targetId ? { ...item, [field]: value } : item
    )
    setAddress({ ...address, phones: updatedPhones })
  }

  const handleAddPhone = (isPhone: boolean): void => {
    setAddress({ ...address, phones: [...address.phones, newExecOrgPhone(orgId, address.id, isPhone)] })
  }

  const handleDeletePhone = (isPhone: boolean): void => {
    const targetId = (isPhone ? phone : fax)?.id
    if (targetId == null) return
    setAddress({ ...address, phones: address.phones.filter(item => item.id !== targetId) })
  }

  const handleCancel = (): void => {
    clearMessages()
    setAddress(savedAddress ?? emptyExecOrgAddress(orgId))
    setDisabled(true)
  }

  const handleSave = (): void => {
    clearMessages()
    if (!usAddressIsValid(address, showErrorMessage)) return
    setSaving(true)
    updateExecOrgAddress(orgId, address.id, address).then(response => {
      if (response.succeeded && response.result != null) {
        setAddress(response.result)
        setSavedAddress(response.result)
        setDisabled(true)
        showSuccessMessage('Address updated successfully.')
      } else {
        showErrorMessage(formatErrorFieldMessages(response.errors))
      }
    }).catch(() => { showErrorMessage('Failed to update organization address.') })
      .finally(() => { setSaving(false) })
  }

  const parentHasContent = parentAddress != null && execOrgAddressHasContent(parentAddress)

  const handleUseDefault = (): void => {
    if (parentAddress == null) return
    clearMessages()
    setAddress(buildContactFromParent(address, parentAddress, orgId))
    showSuccessMessage(`Address populated from ${parentOrgName ?? 'parent organization'}.`)
  }

  return (
    <Box>
      {parentHasContent && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Tooltip title={`Copy address & phone from ${parentOrgName ?? 'the parent organization'}`}>
            <span>
              <Button variant='outlined' size='small' disabled={disabled} onClick={handleUseDefault}>
                Use Default
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
      <PhoneFaxSection
        phone={phone}
        fax={fax}
        disabled={disabled}
        handlePhoneChange={handlePhoneChange}
        handleAddPhone={handleAddPhone}
        handleDeletePhone={handleDeletePhone}
      />

      <Grid container>
        <Grid item xs={5.95}>
          <AddressSlotEditor
            label='Primary'
            slot={address.primary}
            disabled={disabled}
            onSlotChange={(updated) => { updateSlot('primary', updated) }}
            buildings={buildings}
            buildingLoading={buildingLoading}
            mailingChecked={address.mailingAddress === 1}
            onMailingToggle={() => { setAddress({ ...address, mailingAddress: 1 }) }}
          />
        </Grid>
        <Grid item xs={0.1}>
          <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
            <Divider orientation='vertical' flexItem />
          </Box>
        </Grid>
        <Grid item xs={5.95}>
          <AddressSlotEditor
            label='Secondary'
            slot={address.secondary}
            disabled={disabled}
            onSlotChange={(updated) => { updateSlot('secondary', updated) }}
            buildings={buildings}
            buildingLoading={buildingLoading}
            mailingChecked={address.mailingAddress === 2}
            onMailingToggle={() => { setAddress({ ...address, mailingAddress: 2 }) }}
          />
        </Grid>
      </Grid>

      <Box display='flex' justifyContent='flex-end' sx={{ mt: 2 }}>
        <EditActions
          entityName='Address & Phone'
          editing={!disabled}
          saving={saving}
          savePlacement='left'
          onEdit={() => { setDisabled(false) }}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </Box>
    </Box>
  )
}
